import { getBlockType as getLocalBlockType } from "@pathwright/blocks-core/src/blocks/block-types"
import { isEqual } from "lodash"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { getBlockType, getStyles } from "../block/utils"
import { useBlocksConfig } from "../config/BlocksConfigProvider"
import {
  syncedAddBlockAction,
  syncedPasteBlockAction,
  syncedPasteBlocksAction
} from "../editor/actions"
import * as editorActions from "../editor/constants"
import type { EditorStateType } from "../editor/editorState"
import fixtureContext from "../test/blocksContext.json"
import type {
  BlockType,
  BlocksContentType,
  BlocksContextType,
  BlocksChanges
} from "../types"
import { scopeContentData, scopeUserData } from "../utils/scope"
import {
  calculateCompletionProgress,
  createID,
  updateBlocksOrders
} from "../utils/utils"
import * as viewerActions from "../viewer/constants"
import { DefaultError } from "./DefaultError"
import SyncerContext, { type SyncerContextType } from "./SyncerContext"

// Preserve the existing keys and published/draft format so saved work still loads.
const contentPrefix = "pwblocks/content/"
const contentKey = (context: string) => `${contentPrefix}${context}`
const userKey = (context: string, user: string | number) =>
  `${contentKey(context)}/user/${user}`
type StoredContent = BlocksContentType & { draft: BlockType[] }
type UserContent = {
  blocks: Record<string, Pick<BlockType, "data" | "completion">>
}

function readContent(key: string): StoredContent | null {
  const raw = localStorage.getItem(key)
  if (!raw) return null
  const value: StoredContent = JSON.parse(raw)
  if (
    !value ||
    !Array.isArray(value.blocks) ||
    (value.draft != null && !Array.isArray(value.draft))
  )
    throw new Error("Saved local Blocks content is invalid.")
  return { ...value, draft: value.draft ?? value.blocks }
}
function readUserContent(key: string): UserContent {
  const raw = localStorage.getItem(key)
  const value: UserContent | null = raw ? JSON.parse(raw) : null
  return { blocks: value?.blocks || {} }
}
const asError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error))

function getChanges(content: StoredContent, user: string): BlocksChanges {
  const changes: BlocksChanges = {
    createdBy: content.createdBy,
    createdDateTime: content.createdDateTime || 0,
    lastModifiedDateTime: content.lastModifiedDateTime || 0,
    lastPublishedDateTime: content.lastPublishedDateTime || 0,
    blocks: []
  }
  for (const block of content.draft) {
    const published = content.blocks.find((item) => item.id === block.id)
    const type = !published
      ? "added"
      : !isEqual(
          [block.type, block.layout, block.data, block.style],
          [published.type, published.layout, published.data, published.style]
        )
      ? "updated"
      : block.order !== published.order
      ? "reordered"
      : null
    if (type)
      changes.blocks.push({
        type,
        data: {
          ...block,
          lastModifiedBy: user,
          lastModifiedDateTime: changes.lastModifiedDateTime
        }
      })
  }
  for (const block of content.blocks) {
    if (!content.draft.some((item) => item.id === block.id))
      changes.blocks.push({
        type: "removed",
        data: {
          ...block,
          lastModifiedBy: user,
          lastModifiedDateTime: changes.lastModifiedDateTime
        }
      })
  }
  return changes
}

// JSON imports widen literal types and model absent layout keys as undefined.
const localContext: BlocksContextType = {
  ...fixtureContext,
  __typename: "BlocksContext",
  blockTypes: fixtureContext.blockTypes.map(
    ({ layouts, fields, __typename, helpLink, ...type }) => ({
      ...type,
      helpLink: helpLink ?? undefined,
      layouts: Object.fromEntries(
        Object.entries(layouts).filter(([, value]) => value !== undefined)
      ),
      fields: Object.fromEntries(
        Object.entries(fields).filter(([, value]) => value !== undefined)
      )
    })
  )
}

type Props = {
  children: ReactNode
  renderError?: (error: Error) => ReactNode
}

const LocalStorageSession = ({ children, renderError }: Props) => {
  const config = useBlocksConfig()
  const {
    contextKey,
    userID = 1,
    accountID,
    mode = "VIEW",
    isPreviewMode
  } = config
  const callbacks = useRef(config)
  callbacks.current = config
  const blocksContext: BlocksContextType = {
    ...(config.initialBlocks?.blocksContext || localContext),
    accountID: String(accountID),
    userID: String(userID)
  }
  const [content, setContent] = useState<BlocksContentType | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const active = useRef(true)
  const versions = useRef(new Map<string, number>())
  const pendingSaves = useRef(0)

  const readDocument = () => {
    const saved = readContent(contentKey(contextKey))
    if (!saved) throw new Error("Local Blocks document is unavailable.")
    return saved
  }

  const toContent = (saved: StoredContent): BlocksContentType => {
    const { draft, ...published } = saved
    const responses = readUserContent(userKey(contextKey, userID)).blocks
    const blocks = (mode === "EDIT" ? draft : saved.blocks).map((block) => {
      const response = responses[block.id]
      return {
        ...block,
        // Older records stored whole blocks; never restore their stale author data.
        data: {
          ...block.data,
          ...(response
            ? scopeUserData(
                response.data,
                getBlockType(block.type, blocksContext.blockTypes)
              )
            : {})
        },
        completion: response?.completion ||
          block.completion || {
            progress: getLocalBlockType(block.type)?.resolveUserCompletion
              ? 0
              : null,
            score: null
          }
      }
    })
    return {
      ...published,
      contextKey,
      blocks,
      changes: getChanges(saved, String(userID)),
      completion: calculateCompletionProgress({
        blocks,
        completion: { completionToken: "", progress: null }
      })
    }
  }

  useEffect(() => {
    active.current = true
    try {
      let saved = readContent(contentKey(contextKey))
      if (!saved) {
        const now = Date.now()
        const initial = config.initialBlocks?.content
        saved = {
          id: createID(),
          accountID: String(accountID),
          createdBy: String(userID),
          createdDateTime: now,
          lastModifiedDateTime: now,
          lastPublishedDateTime: now,
          ...initial,
          blocks: initial?.blocks || [],
          draft: initial?.blocks || []
        }
        localStorage.setItem(contentKey(contextKey), JSON.stringify(saved))
      }
      const loaded = toContent(saved)
      setContent(loaded)
      callbacks.current.onDataLoaded?.({ content: loaded, blocksContext })
      if (mode === "EDIT") callbacks.current.onSave?.({ content: loaded })
    } catch (error) {
      setError(asError(error))
    }
    return () => {
      active.current = false
    }
    // The outer keyed session reloads for every document/mode/user change.
  }, [])

  const commit = (saved: StoredContent) => {
    const next = toContent(saved)
    localStorage.setItem(contentKey(contextKey), JSON.stringify(saved))
    setContent(next)
    callbacks.current.onSave?.({ content: next })
    return next
  }

  const saveDraft = (state: EditorStateType) => {
    const saved = readDocument()
    const draft = updateBlocksOrders(state.blocks).map((block) => ({
      id: block.id,
      contentID: saved.id,
      type: block.type,
      layout: block.layout,
      order: block.order,
      data: scopeContentData(
        block.data,
        getBlockType(block.type, blocksContext.blockTypes)
      ),
      style: getStyles(block),
      createdBy: block.createdBy || String(userID),
      createdDateTime: block.createdDateTime || Date.now()
    }))
    return commit({
      ...saved,
      draft,
      lastModifiedDateTime: Math.max(
        Date.now(),
        (saved.lastPublishedDateTime || 0) + 1
      )
    })
  }

  const findCopiedBlock = (clip: EditorStateType["copiedBlockClip"]) => {
    if (!clip) throw new Error("Nothing copied to paste.")
    const [documentID, blockID] = clip.object_id.split(":")
    const current = readDocument()
    const find = (saved: StoredContent | null) =>
      saved?.id === documentID
        ? [...saved.draft, ...saved.blocks].find(
            (block) => block.id === blockID
          )
        : undefined
    const currentBlock = find(current)
    if (currentBlock) return currentBlock
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)!
      if (!key.startsWith(contentPrefix) || key.includes("/user/")) continue
      // A malformed unrelated document should not prevent pasting another one.
      let saved: StoredContent | null
      try {
        saved = readContent(key)
      } catch {
        continue
      }
      const block = find(saved)
      if (block) return block
    }
    throw new Error("The copied block is not available in local storage.")
  }

  // Receives post-reducer state, just like GraphQLSyncer.
  const syncer: NonNullable<SyncerContextType["syncer"]> = async (
    action,
    state,
    dispatch
  ) => {
    if (isPreviewMode || !active.current) return
    const { type, payload } = action
    const editorState = state as EditorStateType
    pendingSaves.current += 1
    dispatch({ type: editorActions.START_SYNCING })
    try {
      switch (type) {
        case editorActions.ADD_BLOCK_REQUEST: {
          const block: BlockType = {
            id: createID(),
            contentID: readDocument().id,
            type: payload.type,
            layout: payload.layout,
            data: payload.data || {},
            style: getStyles(null, payload.style),
            order:
              (editorState.addingBlockIndex ?? editorState.blocks.length) + 1
          }
          const next = saveDraft({
            ...editorState,
            ...syncedAddBlockAction(block)(editorState)
          })
          dispatch({
            type: editorActions.SYNCED_ADD_BLOCK,
            payload: { ...block, contentChanges: next.changes }
          })
          break
        }
        case editorActions.PASTE_BLOCK:
        case editorActions.PASTE_BLOCKS: {
          const source = findCopiedBlock(editorState.copiedBlockClip)
          const copies =
            type === editorActions.PASTE_BLOCKS ? payload.copies : 1
          if (!Number.isInteger(copies) || copies < 1)
            throw new Error("Enter a positive number of copies.")
          const copiedBlocks = Array.from(
            { length: copies },
            (_, index): BlockType => ({
              ...source,
              id: createID(),
              contentID: readDocument().id,
              order: payload.index + index + 1,
              data: JSON.parse(JSON.stringify(source.data)),
              completion: undefined,
              contentCompletion: undefined,
              contentChanges: undefined
            })
          )
          const update =
            type === editorActions.PASTE_BLOCKS
              ? syncedPasteBlocksAction({ copiedBlocks })(editorState)
              : syncedPasteBlockAction({ copiedBlock: copiedBlocks[0] })(
                  editorState
                )
          const next = saveDraft({ ...editorState, ...update })
          dispatch({
            type:
              type === editorActions.PASTE_BLOCKS
                ? editorActions.SYNCED_PASTE_BLOCKS
                : editorActions.SYNCED_PASTE_BLOCK,
            payload: {
              copiedBlocks,
              copiedBlock: { ...copiedBlocks[0], contentChanges: next.changes }
            }
          })
          dispatch({
            type: editorActions.SET_CHANGES,
            payload: { changes: next.changes }
          })
          break
        }
        case editorActions.START_PUBLISH: {
          const saved = readDocument()
          const now = Math.max(Date.now(), saved.lastModifiedDateTime || 0)
          const next = commit({
            ...saved,
            blocks: saved.draft,
            lastPublishedDateTime: now,
            lastModifiedDateTime: now
          })
          dispatch({ type: editorActions.SYNCED_PUBLISH, payload: next })
          break
        }
        case editorActions.START_DISCARD_DRAFT: {
          const saved = readDocument()
          const next = commit({
            ...saved,
            draft: saved.blocks,
            lastModifiedDateTime: saved.lastPublishedDateTime
          })
          dispatch({ type: editorActions.SYNCED_DISCARD_DRAFT, payload: next })
          break
        }
        case viewerActions.UPDATE_USER_BLOCK: {
          const block = (state.blocks as BlockType[]).find(
            (item) => item.id === payload.id
          )
          if (!block)
            throw new Error(`Cannot save missing block ${payload.id}.`)
          const responseUser = payload.userID ?? userID
          const key = userKey(contextKey, responseUser)
          const previous = readUserContent(key).blocks[block.id]
          const data = {
            ...previous?.data,
            ...scopeUserData(
              { ...block.data, ...payload.data },
              getBlockType(block.type, blocksContext.blockTypes)
            )
          }
          const version = (versions.current.get(block.id) || 0) + 1
          versions.current.set(block.id, version)
          const completion =
            payload.trackProgress === false
              ? previous?.completion
              : await getLocalBlockType(block.type)?.resolveUserCompletion?.(
                  null,
                  { ...block.data, ...data },
                  block.layout
                )
          if (!active.current || versions.current.get(block.id) !== version)
            return
          const responses = readUserContent(key)
          responses.blocks[block.id] = { data, completion }
          localStorage.setItem(key, JSON.stringify(responses))
          const next = toContent(readDocument())
          const nextBlock = next.blocks.find((item) => item.id === block.id)
          setContent(next)
          callbacks.current.onSave?.({ content: next })
          dispatch({
            type: viewerActions.SYNCED_USER_BLOCK,
            payload: {
              ...block,
              ...nextBlock,
              contentCompletion: next.completion
            }
          })
          break
        }
        case editorActions.DELETE_BLOCK:
        case editorActions.ADD_BLOCK:
        case editorActions.UPDATE_BLOCK_DATA:
        case editorActions.UPDATE_BLOCK_LAYOUT:
        case editorActions.UPDATE_BLOCK_STYLE:
        case editorActions.MOVE_BLOCK_UP:
        case editorActions.MOVE_BLOCK_DOWN: {
          // The first delete action only opens confirmation.
          if (type === editorActions.DELETE_BLOCK && editorState.deleteBlockID)
            break
          const next = saveDraft(editorState)
          const block = next.blocks.find((item) => item.id === payload.id)
          dispatch({
            type:
              block && type.startsWith("UPDATE_BLOCK_")
                ? editorActions.SYNCED_UPDATE_BLOCK
                : editorActions.SYNCED_BLOCKS,
            payload:
              block && type.startsWith("UPDATE_BLOCK_")
                ? { ...block, contentChanges: next.changes }
                : next
          })
          break
        }
      }
    } catch (error) {
      if (type === editorActions.ADD_BLOCK_REQUEST)
        dispatch({ type: editorActions.ADD_BLOCK_ERROR })
      if (
        type === editorActions.PASTE_BLOCK ||
        type === editorActions.PASTE_BLOCKS
      )
        dispatch({
          type: editorActions.PASTE_BLOCK_ERROR,
          payload: { error: asError(error).message }
        })
      else
        dispatch({
          type: editorActions.ERROR,
          payload: { error: asError(error) }
        })
    } finally {
      pendingSaves.current -= 1
      if (active.current)
        dispatch({
          type: pendingSaves.current
            ? editorActions.START_SYNCING
            : editorActions.STOP_SYNCING
        })
    }
  }

  if (error)
    return (
      <>{renderError?.(error) ?? <DefaultError message={error.message} />}</>
    )
  if (!content) return <>{config.renderLoading?.() ?? null}</>
  return (
    <SyncerContext.Provider
      value={{
        content,
        blocksContext,
        blockTypes: blocksContext.blockTypes,
        contentLoading: false,
        needsContentReset: false,
        syncer
      }}
    >
      {children}
    </SyncerContext.Provider>
  )
}

export default function LocalStorageSyncer(props: Props) {
  const { contextKey, accountID, userID, mode, externalForcedUpdateDatetime } =
    useBlocksConfig()
  // Remount the reducer consumers too: they must never retain another user's
  // answers, another document's blocks, or a previous edit/view snapshot.
  return (
    <LocalStorageSession
      key={JSON.stringify([
        accountID,
        contextKey,
        userID,
        mode,
        externalForcedUpdateDatetime
      ])}
      {...props}
    />
  )
}
